import { createApp, watch } from 'vue';
import './index.css';
import './main.css';
import './slider.css';
import App from './App.vue';
import router from './router';
import { App as AppModel } from './models/App';
import { modelRegistry } from 'vue-model';

import VueChatScroll from 'vue-chat-scroll';
import 'boxicons/css/boxicons.min.css';
import { type Device } from './types';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faHandPaper, faPeopleArrows } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import type JitsiMeetJSType from 'JitsiMeetJS';

library.add(faHandPaper, faPeopleArrows);

// JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.DEBUG)
JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.ERROR);

const app = AppModel.create({});

// Start the audio watchdog on the recorder only
if (window.location.hash.includes('config.iAmRecorder=true')) {
  app.startRecorderAudioWatchdog();
}

const unload = (ev: Event) => {
  if (app?.recorderState !== 'off') {
    ev.preventDefault();
    ev.stopImmediatePropagation();
    ev.returnValue = 'Please stop recording before closing' as unknown as boolean;
    return 'Please stop recording before closing';
  } else {
    if (app.isScreenShareEnabled) {
      app.isScreenShareEnabled = false;
    }
    app?.connection.disconnect();
  }
};

window.addEventListener('beforeunload', unload);

const initOptions = {
  disableAudioLevels: false,
  // enableWindowOnErrorHandler: true
};

JitsiMeetJS.init(initOptions);

// Recorder only: capture detailed lib-jitsi-meet logs for troubleshooting
if (window.location.hash.includes('config.iAmRecorder=true')) {
  JitsiMeetJS.setLogLevel(JitsiMeetJS.logLevels.INFO);
  JitsiMeetJS.setLogLevelById(JitsiMeetJS.logLevels.DEBUG, 'xmpp:JingleSessionPC');
}

document.addEventListener('drop', (e) => e.preventDefault(), { capture: true });

createApp(App).use(VueChatScroll).use(router).component('font-awesome-icon', FontAwesomeIcon).mount('#app');

// const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || window.webkitConvertPointFromNodeToPage !== undefined

// if (isSafari) {
//   router.replace('/unsupported-browser')
// }

const browserIsFF = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

if (JitsiMeetJS.mediaDevices.isDeviceChangeAvailable()) {
  JitsiMeetJS.mediaDevices.enumerateDevices((devices: Device[]) => {
    // if there is no deviceId, we keep trying until we get a device
    // FIXME: Firefox is deniying any access to the device even if its allowed when the camera source is blocked somehow
    // exmaples are: another app using the camera (e.g. chrome) or a missconnected usb cam
    if (!devices[0]?.deviceId) {
      const interval = setInterval(() => {
        JitsiMeetJS.mediaDevices.enumerateDevices((devices: Device[]) => {
          if (devices[0]?.deviceId) {
            clearInterval(interval);
            app.devices = devices;

            const device = app.devices.find((device) => device.kind === 'audioinput' && device.deviceId);
            if (device && browserIsFF) {
              app.micDeviceId = device.deviceId;
              app.connection?.currentRoom?.refreshLocalTracks();
            }
          }
        });
      }, 200);
    } else {
      app.devices = devices;

      const device = app.devices.find((device) => device.kind === 'audioinput' && device.deviceId);
      if (device && browserIsFF) {
        app.micDeviceId = device.deviceId;
        app.connection?.currentRoom?.refreshLocalTracks();
      }
    }
  });
}

// JitsiMeetJS.mediaDevices.addEventListener(JitsiMeetJS.events.mediaDevices.DEVICE_LIST_CHANGED, (devices: Device[]) => {
//   app.devices = devices;
//   // if (JitsiMeetJS.mediaDevices.isDeviceListAvailable()) {
//   //   JitsiMeetJS.mediaDevices.enumerateDevices((devices: Device[]) => {
//   //     app.devices = devices
//   //   })
//   // }
// });

const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)() as AudioContext;
const gainNode = audioCtx.createGain();

app.gainNode = gainNode;
app.audioCtx = audioCtx;

watch(
  () => app.inputGain,
  (newValue) => {
    gainNode.gain.setValueAtTime(newValue, audioCtx.currentTime);
  },
);

declare global {
  const JitsiMeetJS: typeof JitsiMeetJSType;
  interface Window {
    app: AppModel;
    models: typeof modelRegistry;
    APP: {
      conference: {
        _room: {
          joined: boolean;
          isJoined(): boolean;
        };
      };
    };
  }
}

window.app = app;
window.models = modelRegistry;
window.APP = {
  conference: {
    _room: {
      joined: false,
      isJoined() {
        return this.joined;
      },
    },
  },
};
